/**
 * `account_create` renderer — composes `@nextday/funnel-ui` `AccountCreate`
 * (email form with optional SSO buttons).
 *
 * The renderer forwards every user action through `onAnswer` with a
 * structured payload, leaving the actual auth side-effects (calling
 * Kinde's `registerWithConnection`, etc.) to the orchestrator. This keeps
 * the renderer brand-agnostic — survey-kit never imports `@nextday/brand-auth`.
 *
 * Answer payloads (action-tagged so a single answerKey can carry the full
 * lifecycle without a key explosion):
 *   { value: <email>,        action: "submit_email" }
 *   { value: null,            action: "continue_with_google" }
 *   { value: null,            action: "continue_with_apple" }
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "account_create",
 *     "data": {
 *       "answerKey": "account_create",
 *       "question": "Create your account",
 *       "description": "We'll send a 6-digit code to verify your email.",
 *       "ssoProviders": ["google", "apple"]
 *     }
 *   }
 */
import { AccountCreate } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";
import { urlOrTemplate } from "@survey-kit/urlOrTemplate";

const ssoProviderSchema = z.union([z.literal("google"), z.literal("apple")]);

export const accountCreatePresentationSchema = z.enum(["default", "drawer"]);

export type AccountCreatePresentation = z.infer<typeof accountCreatePresentationSchema>;

export const accountCreateSchema = z
  .object({
    answerKey: z.string().min(1),
    question: z.string().min(1).optional(),
    description: z.string().optional(),
    eyebrow: z.string().optional(),
    portraitUrl: urlOrTemplate.optional(),
    prefilledEmail: z.string().optional(),
    emailPlaceholder: z.string().optional(),
    ctaLabel: z.string().optional(),
    termsUrl: urlOrTemplate.optional(),
    privacyUrl: urlOrTemplate.optional(),
    isSubmitting: z.boolean().optional(),
    errorMessage: z.string().optional(),
    ssoProviders: z.array(ssoProviderSchema).optional(),
    /** When `"drawer"`, the step renders as a bottom sheet over the previous step. */
    presentation: accountCreatePresentationSchema.optional(),
    /** When `"before_email"`, SSO buttons render above the email input. */
    ssoPosition: z.enum(["before_email", "after_email"]).optional(),
    /** Custom text for the separator between SSO and email sections. */
    separatorLabel: z.string().optional(),
    /** Render the CTA button inline with the email input. */
    inlineCta: z.boolean().optional(),
    /** Custom disclaimer text shown below the form (replaces legal footer). */
    disclaimer: z.string().optional(),
  })
  .passthrough();

export type AccountCreateData = z.infer<typeof accountCreateSchema>;

const ACTIONS = {
  submitEmail: "submit_email",
  continueWithGoogle: "continue_with_google",
  continueWithApple: "continue_with_apple",
} as const;

const AccountCreateRenderer = ({ data, step, onAnswer }: RendererProps<AccountCreateData>) => {
  const includesProvider = (provider: "google" | "apple"): boolean =>
    !!data.ssoProviders && data.ssoProviders.includes(provider);

  return (
    <AccountCreate
      question={data.question}
      description={data.description}
      eyebrow={data.eyebrow}
      portraitUrl={data.portraitUrl}
      prefilledEmail={data.prefilledEmail}
      emailPlaceholder={data.emailPlaceholder}
      ctaLabel={data.ctaLabel}
      termsUrl={data.termsUrl}
      privacyUrl={data.privacyUrl}
      isSubmitting={data.isSubmitting}
      errorMessage={data.errorMessage}
      ssoPosition={data.ssoPosition}
      separatorLabel={data.separatorLabel}
      inlineCta={data.inlineCta}
      disclaimer={data.disclaimer}
      onSubmitEmail={(email) =>
        onAnswer(data.answerKey, { value: email, action: ACTIONS.submitEmail })
      }
      onContinueWithGoogle={
        includesProvider("google")
          ? () =>
              onAnswer(data.answerKey, {
                value: null,
                action: ACTIONS.continueWithGoogle,
              })
          : undefined
      }
      onContinueWithApple={
        includesProvider("apple")
          ? () =>
              onAnswer(data.answerKey, {
                value: null,
                action: ACTIONS.continueWithApple,
              })
          : undefined
      }
      testId={`renderer-account-create-${step.id}`}
    />
  );
};

export const accountCreateRenderer = createRenderer({
  type: "account_create",
  dataSchema: accountCreateSchema,
  label: "Account create",
  Component: AccountCreateRenderer,
});

export default accountCreateRenderer;
